/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Integrated all lookup data (vendors, families, barcodes, currencies, UOM).
 *                                   - Implemented client-side validation for required fields.
 *                                   - Adjusted required fields based on product type (standard vs. composite).
 *                                   - Corrected `article_number` input type to `text`.
 *                                   - Added `min="0.000001"` to `purchase_quantity`.
 *                                   - Removed local error prop, relying on global error handling.
 *                                   - Integrated Spinner for loading states.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide fields
 *                                   and customize field titles based on whitelabel configuration.
 *                                   - Added a new 'Maximize Profit' modal, triggered by an icon in the table.
 *                                   - The modal allows users to input a target margin and displays
 *                                     raw data from the `/maximize/{product_id}/{margin}` endpoint,
 *                                     refetching on margin changes.
 *                                   - Enhanced the display of the maximize profit response to be structured
 *                                     and human-readable, showing the maximum cost and detailed margins.
 *
 * -----
 * SECURITY_RISK: LOW - This component handles user input for product definitions and displays derived data.
 *                      It does not process sensitive data directly. The new 'Maximize Profit' modal
 *                      fetches data from a backend endpoint, but the UI only displays it.
 * INTENT: Provide a comprehensive and reusable form for creating and editing Product records,
 *         supporting both standard and composite product types, with robust validation
 *         and integration of various lookup data, now with whitelabeling capabilities and
 *         a new feature to explore profit maximization scenarios.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure all lookup data is correctly integrated,
 *                        validation logic is comprehensive for both product types, and
 *                        the form state correctly reflects submission and lookup status.
 *                        Ensure the Maximize Profit modal correctly fetches and displays data,
 *                        and handles user input for margin.
 * LLM_CONFIDENCE: 95% - The form is complex due to conditional fields and extensive lookups,
 *                        but the implementation is robust. The new Maximize Profit feature
 *                        is integrated correctly with state management and API calls.
 */
import { useState, useCallback, useEffect } from 'react';
import { Product, ProductCreate, ProductUpdate, MaximizeResponse } from '../types/costwand.d'; // NEW: Import MaximizeResponse
import { useProduct } from '../hooks/useProduct.ts';
import ProductForm from '../components/ProductForm.tsx';
import ProductTable from '../components/ProductTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog.tsx';

// Import new lookup hooks
import { useVendorLookup } from '../hooks/useVendorLookup.ts';
import { useFamilyLookup } from '../hooks/useFamilyLookup.ts';
import { useSubFamilyLookup } from '../hooks/useSubFamilyLookup.ts';
import { useBarcodeType1Lookup } from '../hooks/useBarcodeType1Lookup.ts';
import { useBarcodeType2Lookup } from '../hooks/useBarcodeType2Lookup.ts';
import { useCurrencyLookup } from '../hooks/useCurrencyLookup.ts';
import { useUnitOfMeasureLookup } from '../hooks/useUnitOfMeasureLookup.ts';
import { Spinner } from '../components/ui/spinner.tsx'; // Import Spinner
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';
import { Maximize } from 'lucide-react'; // NEW: Import Maximize icon
import { Input } from '../components/ui/input.tsx'; // NEW: Import Input for margin
import { Label } from '@radix-ui/react-label'; // NEW: Import Label for margin input
import { apiClient, ApiError } from '../api/apiClient.tsx'; // NEW: Import apiClient and ApiError

const ProductsPage = () => {
  const {
    products,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createProduct,
    updateProduct,
    deleteProduct,
  } = useProduct();

  const navigate = useNavigate(); // Initialize useNavigate

  // Initialize all lookup hooks
  const { vendors, loading: vendorsLoading, error: vendorsError } = useVendorLookup();
  const { families, loading: familiesLoading, error: familiesError } = useFamilyLookup();
  const { subFamilies, loading: subFamiliesLoading, error: subFamiliesError } = useSubFamilyLookup();
  const { barcodeType1s, loading: barcodeType1sLoading, error: barcodeType1sError } = useBarcodeType1Lookup();
  const { barcodeType2s, loading: barcodeType2sLoading, error: barcodeType2sError } = useBarcodeType2Lookup();
  const { currencies, loading: currenciesLoading, error: currenciesError } = useCurrencyLookup();
  const { unitsOfMeasure, loading: unitsOfMeasureLoading, error: unitsOfMeasureError } = useUnitOfMeasureLookup();

  const { whitelabelConfig } = useWhitelabelContext();

  // Combine all lookup loading states
  const allLookupsLoading =
    vendorsLoading ||
    familiesLoading ||
    subFamiliesLoading ||
    barcodeType1sLoading ||
    barcodeType2sLoading ||
    currenciesLoading ||
    unitsOfMeasureLoading;

  // Combine all lookup errors
  const allLookupsError =
    vendorsError ||
    familiesError ||
    subFamiliesError ||
    barcodeType1sError ||
    barcodeType2sError ||
    currenciesError ||
    unitsOfMeasureError;


  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // NEW: State for Maximize Profit modal
  const [showMaximizeModal, setShowMaximizeModal] = useState<boolean>(false);
  const [selectedProductForMaximize, setSelectedProductForMaximize] = useState<Product | null>(null);
  const [maximizeMargin, setMaximizeMargin] = useState<number>(50); // Default margin
  const [maximizeData, setMaximizeData] = useState<any | null>(null); // Raw response
  const [maximizeLoading, setMaximizeLoading] = useState<boolean>(false);
  const [maximizeError, setMaximizeError] = useState<string | null>(null);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleDeleteProduct = async (id: number) => {
    await deleteProduct(id);
  };

  const getEntityTitle = (defaultTitle: string, singular: boolean) =>
    (singular && whitelabelConfig.entities?.['products'].title_singular) || whitelabelConfig.entities?.['products'].title || defaultTitle;

  // NEW: Function to fetch maximize data
  const fetchMaximizeData = useCallback(async (productId: number, margin: number) => {
    setMaximizeLoading(true);
    setMaximizeError(null);
    try {
      const response = await apiClient<MaximizeResponse>(`/maximize/${productId}/${margin}`, { bypassCache: true });
      setMaximizeData(response); // Store the raw response
    } catch (err: any) {
      setMaximizeError(err.message || 'Failed to fetch maximize data.');
    } finally {
      setMaximizeLoading(false);
    }
  }, []);

  // NEW: Handler for clicking the maximize icon
  const handleMaximizeClick = useCallback((product: Product) => {
    setSelectedProductForMaximize(product);
    setMaximizeMargin(50); // Reset to default margin
    setMaximizeData(null); // Clear previous data
    setMaximizeError(null); // Clear previous error
    setShowMaximizeModal(true);
    // Fetch data immediately for the selected product with default margin
    if (product.id) {
      fetchMaximizeData(product.id, 50);
    }
  }, [fetchMaximizeData]);

  // NEW: Effect to refetch maximize data when margin or selected product changes
  useEffect(() => {
    if (showMaximizeModal && selectedProductForMaximize?.id) {
      // Only refetch if margin actually changed or it's the initial load for the modal
      // The initial fetch is handled by handleMaximizeClick, subsequent changes by this effect
      const handler = setTimeout(() => {
        fetchMaximizeData(selectedProductForMaximize.id!, maximizeMargin);
      }, 300); // Debounce to avoid too many requests on rapid input changes
      return () => clearTimeout(handler);
    }
  }, [maximizeMargin, selectedProductForMaximize, showMaximizeModal, fetchMaximizeData]);


  // NEW: Handler for margin input change in the modal
  const handleMaximizeMarginChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setMaximizeMargin(value);
    }
  }, []);

  // NEW: Handler for closing the maximize modal
  const handleCloseMaximizeModal = useCallback(() => {
    setShowMaximizeModal(false);
    setSelectedProductForMaximize(null);
    setMaximizeData(null);
    setMaximizeError(null);
    setMaximizeMargin(50); // Reset margin
  }, []);

  // NEW: Consolidated handler for product creation/update
  const handleFormSubmit = async (data: ProductCreate | ProductUpdate, isComposite: boolean): Promise<boolean | number> => {
    setFormSubmitting(true);

    try {
      let result: number | boolean;
      if (editingProduct) {
        // This is an update operation. The switch is hidden for edits, so isComposite will be false.
        const updateData: ProductUpdate = {
          ...data,
          updated_by: 'tester',
        };
        result = await updateProduct(editingProduct.id!, updateData);
      } else {
        // This is a creation operation
        const createData: ProductCreate = {
          ...data as ProductCreate, // Cast to ProductCreate for creation
          created_by: 'tester',
        };

        result = await createProduct(createData);

        if (isComposite && typeof result === 'number') {
          // If it was a composite creation, redirect to composition edit page
          setIsFormOpen(false); // Close form on success
          navigate(`/composition-edit/${result}`);
          return result; // Return the new product ID
        }
      }

      if (result) { // If update was successful or standard product created
        setIsFormOpen(false); // Close form on success
        return result; // Return true for update, or new product ID for standard creation
      } else {
        return false; // Operation failed
      }
    } catch (e: any) {
      // Global error handler will catch this.
      return false; // Indicate failure
    } finally {
      setFormSubmitting(false);
    }
  };


  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">{getEntityTitle('Product')} Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddProduct} disabled={allLookupsLoading}>
              {allLookupsLoading ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" /> Loading form...
                </span>
              ) : (
                'Add New ' + getEntityTitle('Product', true)
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              <DialogDescription>
                {editingProduct ? 'Make changes to the product here.' : 'Add a new product record.'}
              </DialogDescription>
            </DialogHeader>
            {allLookupsError && (
              <p className="text-destructive text-sm p-2 bg-red-100 border border-red-400 rounded">
                Error loading lookup data: {allLookupsError}
              </p>
            )}
            <ProductForm
              initialData={editingProduct}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              vendors={vendors}
              families={families}
              subFamilies={subFamilies}
              barcodeType1s={barcodeType1s}
              barcodeType2s={barcodeType2s}
              currencies={currencies}
              unitsOfMeasure={unitsOfMeasure}
              lookupLoading={allLookupsLoading}
              entityId="products"
            />
          </DialogContent>
        </Dialog>
      </div>

      <ProductTable
        products={products}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditProduct}
        onDelete={handleDeleteProduct}
        loading={loading}
        error={error}
        lookupLoading={allLookupsLoading}
        baseEndpoint="/products" // NEW: Pass the base endpoint
        entityId="products"
        onMaximizeClick={handleMaximizeClick} // NEW: Pass the handler to ProductTable
      />

      {/* NEW: Maximize Profit Modal */}
      <Dialog open={showMaximizeModal} onOpenChange={handleCloseMaximizeModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Maximize Profit for {selectedProductForMaximize?.description || 'Product'}</DialogTitle>
            <DialogDescription>
              View profit maximization details for a given margin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="maximize-margin" className="block text-sm font-medium text-foreground mb-1">
                Target Margin Loss (%)
              </Label>
              <Input
                id="maximize-margin"
                type="number"
                step="1"
                value={maximizeMargin}
                onChange={handleMaximizeMarginChange}
                disabled={maximizeLoading}
              />
            </div>

            {maximizeLoading ? (
              <div className="flex items-center justify-center h-32">
                <Spinner size="md" /> <span className="ml-2">Calculating profit maximization...</span>
              </div>
            ) : maximizeError ? (
              <div className="text-destructive p-4 bg-red-100 border border-red-400 rounded-md">
                <p className="font-semibold">Error:</p>
                <p>{maximizeError}</p>
              </div>
            ) : maximizeData && maximizeData['0'] === null ? ( // MODIFIED: Added condition for maximizeData['0'] === null
              <div className="bg-blue-50 p-4 rounded-md text-blue-800">
                <p className="font-semibold">
                  This {whitelabelConfig.entities?.['products']?.title_singular || 'product'} is not currently affecting any {whitelabelConfig.entities?.['composites']?.title || 'composite products'}.
                </p>
                <p className="text-sm mt-2">
                  Maximize profit calculations are only relevant for components within composite products.
                </p>
              </div>
            ) : maximizeData ? (
              <div className="bg-gray-50 p-4 rounded-md">
                {maximizeData['0'] === true ? (
                  <>
                    <p className="mb-4">
                      <span className="font-medium">Maximum Cost Before Losing Margin:</span>{' '}
                      <span className="font-bold text-xl">${(maximizeData['1'] as number).toFixed(2)}</span>
                    </p>
                    <h3 className="text-md font-semibold mb-2">Detailed Margins at this Cost:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(maximizeData['2'] as { [key: string]: { cost: number; price: number; profit: number; margin: number } }).map(([productName, metrics]) => (
                        <div key={productName} className="border p-3 rounded-md bg-white shadow-sm">
                          <p className="font-bold text-blue-700">{productName}</p>
                          <p>Cost: ${metrics.cost.toFixed(2)}</p>
                          <p>Price: ${metrics.price.toFixed(2)}</p>
                          <p>Profit: ${metrics.profit.toFixed(2)}</p>
                          <p>Margin: {metrics.margin.toFixed(2)}%</p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-red-600">
                    <p className="font-semibold">Calculation Failed or No Data:</p>
                    <p>The maximize operation did not return a successful result.</p>
                    <p className="mt-2">Raw Response: <pre className="bg-gray-800 text-white p-2 rounded-md">{JSON.stringify(maximizeData, null, 2)}</pre></p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">No data available. Adjust margin to see results.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseMaximizeModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;